import React, { useLayoutEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import TailwindModal from '../ui/TailwindModal'
import { highlightCost } from '../modalCostHighlight'

// Shared palette first — Alert.css reads its tokens from :root.
import '../modalTheme.css'
import './Alert.css'

/** Must match the exit animation duration in Alert.css. */
const EXIT_MS = 140

/**
 * Severity of an alert, as passed by every `onShowAlert(msg, flg)` caller. Drives the accent colour
 * and default header so a rejected action does not look identical to a confirmed one.
 */
export type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

const SEVERITIES: readonly AlertSeverity[] = ['success', 'error', 'warning', 'info']

function normalizeSeverity(value: unknown): AlertSeverity {
  return SEVERITIES.includes(value as AlertSeverity) ? (value as AlertSeverity) : 'info'
}

/** Header text per severity; overridden by an explicit `options.title`. */
const SEVERITY_TITLE_KEY: Record<AlertSeverity, { key: string; fallback: string }> = {
  success: { key: 'alert.titleSuccess', fallback: 'Success' },
  error: { key: 'alert.titleError', fallback: 'Failed' },
  warning: { key: 'alert.titleWarning', fallback: 'Warning' },
  info: { key: 'alert.title', fallback: 'Notification' },
}

interface AlertState {
  visible: boolean
  text: string
  /** Overrides the default localized "Notification" header for this alert only. */
  title?: string
  severity: AlertSeverity
  resolve?: () => void
}

export type AlertOpenOptions = {
  title?: string
  severity?: AlertSeverity
}

let showAlert: ((text: string, options?: AlertOpenOptions) => Promise<void>) | undefined

export const AlertHost: React.FC = () => {
  const { t } = useTranslation()
  const [alertState, setAlertState] = useState<AlertState>({
    visible: false,
    text: '',
    severity: 'info',
  })

  useLayoutEffect(() => {
    showAlert = (text: string, options?: AlertOpenOptions) =>
      new Promise<void>((resolve) => {
        setAlertState({
          visible: true,
          text,
          title: options?.title,
          severity: normalizeSeverity(options?.severity),
          resolve,
        })
      })

    return () => {
      showAlert = undefined
    }
  }, [])

  const handleClose = () => {
    setAlertState((previousState) => {
      previousState.resolve?.()
      // Keep `text` so the message stays put while the modal animates out.
      return { ...previousState, visible: false, resolve: undefined }
    })
  }

  return (
    <TailwindModal
      open={alertState.visible}
      onClose={handleClose}
      zIndex={9999}
      exitMs={EXIT_MS}
      aria-labelledby="app-alert-title"
      aria-describedby="app-alert-message"
      backdropClassName="alert-backdrop"
    >
      <section
        className={`alert-modal alert-modal--${alertState.severity}`}
        role="dialog"
        aria-modal="true"
      >
        <header className="alert-modal__header">
          <h2 id="app-alert-title" className="alert-modal__title">
            {alertState.title ??
              t(SEVERITY_TITLE_KEY[alertState.severity].key, {
                defaultValue: SEVERITY_TITLE_KEY[alertState.severity].fallback,
              })}
          </h2>

          <button
            className="alert-modal__close"
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={handleClose}
          />
        </header>

        <div className="alert-modal__content">
          <p id="app-alert-message" className="alert-modal__message">
            {highlightCost(alertState.text, 'alert-modal__cost')}
          </p>
        </div>

        <div className="alert-modal__ornament" aria-hidden="true">
          <span className="alert-modal__ornament-line" />
          <span className="alert-modal__ornament-center">
            <i className="alert-modal__ornament-dot alert-modal__ornament-dot--left" />
            <i className="alert-modal__ornament-diamond alert-modal__ornament-diamond--left-outer" />
            <i className="alert-modal__ornament-diamond alert-modal__ornament-diamond--left-inner" />
            <i className="alert-modal__ornament-diamond alert-modal__ornament-diamond--main" />
            <i className="alert-modal__ornament-diamond alert-modal__ornament-diamond--right-inner" />
            <i className="alert-modal__ornament-diamond alert-modal__ornament-diamond--right-outer" />
            <i className="alert-modal__ornament-dot alert-modal__ornament-dot--right" />
          </span>
          <span className="alert-modal__ornament-line" />
        </div>

        <footer className="alert-modal__actions">
          <button className="alert-modal__button" type="button" onClick={handleClose} autoFocus>
            {t('common.ok')}
          </button>
        </footer>
      </section>
    </TailwindModal>
  )
}

export const alert = {
  open: async (text: string, options?: AlertOpenOptions) => {
    if (!showAlert) {
      console.warn('Alert not initialized yet.')
      return
    }

    await showAlert(text, options)
  },
}
